/**
 * M3C-04 — step 0 "pick a workflow" (DI-D62 c1: work starts by picking a
 * workflow; the version's start node decides the first screen).
 *
 * The options are the server's (`workflows/v1`): own workflows by name with
 * the kind words of their injection node's served facts, and the injection nodes shared
 * with the tenant, labelled by their OWNER ("Zeitview workflow — shared by
 * Zeitview", XT-D9). Exactly one option → it is preselected and the step
 * collapses to one line; none → the entry is blocked and names the tenant
 * admin (as DI-2-12 did for the drop). With several, the caller preselects
 * the tenant's default (`isDefault`) and the user may change it.
 *
 * DI-5-3 — a version with a client node this browser cannot serve
 * (`missingRequirements` over the SERVED `clientRequirements`) is listed but
 * MARKED ("needs Chrome or Edge") and cannot be picked; its tooltip is the
 * message. When no option is left to pick, the step shows the message in
 * place of the collapsed line (the "none → blocked" pattern). The caller
 * passes the browser's capabilities; without them every option is pickable.
 *
 * A project that already exists brings its own version (the project-first
 * path skips this step); an edit never shows it (DI-D9 c3: a version changes
 * through the Workflow panel only).
 */
import type { ReactElement } from "react";
import {
  Alert,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { compactRadioLabelSlot } from "@aeriosiq/ui";
import {
  injectionFactsOf,
  injectionKindWords,
  sharedWorkflowLabel,
  type Workflow,
} from "./api/workflows.js";
import {
  ALL_CAPABLE,
  missingRequirements,
  NEEDS_CHROMIUM_MARK,
  unsupportedBrowserLine,
  type ClientCapabilities,
  type MissingRequirement,
} from "../intake/capabilities/fsa.js";
import { UnsupportedBrowserNotice } from "../intake/capabilities/UnsupportedBrowserNotice.js";

export interface WorkflowOption {
  versionId: string;
  label: string;
  /** The kind words of the injection node's served facts ("images uploaded"). */
  words: string | null;
  shared: boolean;
  isDefault: boolean;
  /** DI-5-3 — the client nodes this browser cannot serve; `[]` = pickable. */
  missing: MissingRequirement[];
}

export function workflowOptionsOf(
  list: readonly Workflow[],
  caps: ClientCapabilities = ALL_CAPABLE,
): WorkflowOption[] {
  return list.map((w) => ({
    versionId: w.latestVersion.versionId,
    label: w.shared ? sharedWorkflowLabel(w) : w.name,
    words: injectionKindWords(injectionFactsOf(w.latestVersion)),
    shared: w.shared,
    isDefault: w.isDefault,
    missing: missingRequirements(w.latestVersion, caps),
  }));
}

/** DI-5-3 — may this option be picked in this browser? */
export function isPickable(o: Pick<WorkflowOption, "missing">): boolean {
  return o.missing.length === 0;
}

/**
 * The option a fresh step starts with: the only one, else the default —
 * among the PICKABLE ones (DI-5-3: a refused version is never preselected).
 */
export function preselectedOption(
  options: readonly WorkflowOption[],
): string | null {
  const pickable = options.filter(isPickable);
  if (pickable.length === 1) return pickable[0]?.versionId ?? null;
  return pickable.find((o) => o.isDefault)?.versionId ?? null;
}

const BLOCKED_WORDS: Record<"project" | "drop", string> = {
  project:
    "Your company has no workflow to start a project with — ask your tenant admin to set one up.",
  drop: "Your company has no workflow to start a drop with — ask your tenant admin to set one up.",
};

export function blockedWords(entry: "project" | "drop"): string {
  return BLOCKED_WORDS[entry];
}

export interface PickWorkflowStepProps {
  entry: "project" | "drop";
  options: readonly WorkflowOption[];
  value: string | null;
  onChange: (versionId: string) => void;
  disabled?: boolean;
}

function OptionLabel({ o }: { o: WorkflowOption }): ReactElement {
  return (
    <Stack component="span" spacing={0}>
      <Typography component="span" variant="body2">
        {o.label}
        {o.words ? (
          <Typography component="span" variant="inherit" color="text.secondary">
            {" "}
            · {o.words}
          </Typography>
        ) : null}
        {isPickable(o) ? null : (
          <Typography
            component="span"
            variant="inherit"
            color="warning.main"
            data-testid="pick-workflow-needs-chromium"
          >
            {" "}
            · {NEEDS_CHROMIUM_MARK}
          </Typography>
        )}
      </Typography>
    </Stack>
  );
}

export function PickWorkflowStep({
  entry,
  options,
  value,
  onChange,
  disabled = false,
}: PickWorkflowStepProps): ReactElement {
  if (options.length === 0) {
    return (
      <Alert
        severity="warning"
        variant="outlined"
        data-testid="pick-workflow-blocked"
      >
        {blockedWords(entry)}
      </Alert>
    );
  }
  // DI-5-3 — nothing left to pick in this browser: the message, no pick
  if (!options.some(isPickable)) {
    return (
      <div data-testid="pick-workflow-unsupported">
        <UnsupportedBrowserNotice missing={options.flatMap((o) => o.missing)} />
      </div>
    );
  }
  const [only] = options;
  if (options.length === 1 && only) {
    return (
      <Typography
        variant="body2"
        data-testid="pick-workflow-line"
        component="p"
      >
        <Typography component="span" variant="inherit" color="text.secondary">
          Workflow:{" "}
        </Typography>
        <strong>{only.label}</strong>
        {only.words ? (
          <Typography component="span" variant="inherit" color="text.secondary">
            {" "}
            · {only.words}
          </Typography>
        ) : null}
      </Typography>
    );
  }
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, px: 2.5, py: 1.5 }}
      data-testid="pick-workflow-step"
    >
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{ fontWeight: 600 }}
        id={`pick-workflow-${entry}-label`}
      >
        Workflow
      </Typography>
      <RadioGroup
        aria-labelledby={`pick-workflow-${entry}-label`}
        value={value ?? ""}
        onChange={(_e, v) => {
          // DI-5-3 — a marked option never becomes the pick (belt and braces
          // over the disabled radio)
          const o = options.find((x) => x.versionId === v);
          if (o && !isPickable(o)) return;
          onChange(v);
        }}
      >
        {options.map((o) => (
          <FormControlLabel
            key={o.versionId}
            value={o.versionId}
            control={<Radio size="small" />}
            disabled={disabled || !isPickable(o)}
            slotProps={compactRadioLabelSlot}
            data-testid={
              o.shared ? "pick-workflow-shared" : "pick-workflow-own"
            }
            title={
              isPickable(o) ? undefined : unsupportedBrowserLine(o.missing)
            }
            label={<OptionLabel o={o} />}
          />
        ))}
      </RadioGroup>
    </Paper>
  );
}
